import { Box, Center, Loader, Select, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntityOption } from '../types';
import classes from './AsyncEntitySelect.module.css';

interface AsyncEntitySelectProps {
  provider: {
    fetch: ( query: string, offset: number, limit: number ) => Promise<EntityOption[]>;
    pageSize: number;
  };
  selectedOption: EntityOption | null;
  onSelect: ( option: EntityOption | null ) => void;
}

export const AsyncEntitySelect: React.FC<AsyncEntitySelectProps> = ({
  provider,
  selectedOption,
  onSelect
}) => {
  const { t } = useTranslation( 'rules' );
  const [ options, setOptions ] = useState<EntityOption[]>([]);
  const [ searchQuery, setSearchQuery ] = useState( '' );
  const [ loading, setLoading ] = useState( false );
  const [ hasMore, setHasMore ] = useState( true );
  const scrollRef = useRef<HTMLDivElement>( null );
  const [ isOpen, setIsOpen ] = useState( false );

  // Debounce search query to avoid too many API calls
  const [debouncedQuery] = useDebouncedValue( searchQuery, 300 );

  const loadMore = async ( reset = false ) => {
    if ( loading || ( !hasMore && !reset )) return;

    setLoading( true );
    const offset = reset ? 0 : options.length;

    try {
      const newOptions = await provider.fetch( debouncedQuery, offset, provider.pageSize );

      setOptions( prev => reset ? newOptions : [ ...prev, ...newOptions ]);
      setHasMore( newOptions.length === provider.pageSize );
    } catch ( error ) {
      console.error( 'Error loading entities:', error );
      setOptions( reset ? [] : options );
      setHasMore( false );
    } finally {
      setLoading( false );
    }
  };

  const handleScroll = () => {
    const element = scrollRef.current;
    if ( !element || loading || !hasMore ) return;

    const { scrollTop, scrollHeight, clientHeight } = element;
    // Load more when scrolled near the bottom (50px threshold)
    if ( scrollHeight - scrollTop - clientHeight < 50 ) {
      loadMore();
    }
  };

  // Reset and load when search query changes
  useEffect(() => {
    loadMore( true );
  }, [debouncedQuery]);

  // Initial load when dropdown opens
  useEffect(() => {
    if ( isOpen && options.length === 0 && !loading ) {
      loadMore( true );
    }
  }, [isOpen]);

  return (
    <Stack gap="xs">
      <TextInput
        label={t( 'preview_entity_selector_label', 'Select entity for preview' )}
        placeholder={t( 'preview_entity_search_placeholder', 'Search...' )}
        value={searchQuery}
        onChange={( e ) => setSearchQuery( e.target.value )}
        radius="xs"
        size="sm"
      />

      {selectedOption && (
        <Box className={classes.selectedItem}>
          <Text size="sm" fw={500}>{selectedOption.label}</Text>
          <UnstyledButton
            onClick={() => onSelect( null )}
            className={classes.clearButton}
          >
            <Text size="xs" c="dimmed">{t( 'clear', 'Clear' )}</Text>
          </UnstyledButton>
        </Box>
      )}

      {( searchQuery || !selectedOption ) && (
        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          className={classes.scrollContainer}
        >
          {options.length === 0 && !loading ? (
            <Center p="md">
              <Text size="sm" c="dimmed">
                {t( 'no_results', 'No results found' )}
              </Text>
            </Center>
          ) : (
            <Stack gap={2}>
              {options.map( opt => (
                <UnstyledButton
                  key={opt.id}
                  onClick={() => {
                    onSelect( opt );
                    setSearchQuery( '' );
                  }}
                  className={classes.optionButton}
                  data-selected={selectedOption?.id === opt.id}
                >
                  <Text size="sm">{opt.label}</Text>
                </UnstyledButton>
              ))}
            </Stack>
          )}

          {loading && (
            <Center p="md">
              <Loader size="sm" />
            </Center>
          )}
        </Box>
      )}
    </Stack>
  );
};
